import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { isPickerImage as supportsPickerImage } from "../constants/file_types"
import {
  currentDetectionResponse,
  initialTargetFaceSelection,
  normalizeDetectedFaces,
  selectTargetFace,
  serializeTargetPickerSubmission
} from "../lib/target_face_selection_state.mjs"

export default class extends Controller {
  static targets = ["hiddenFields", "status", "submitButton"]
  static values = {
    directUploadUrl: String,
    detectUrl: String,
    cancelUrl: String,
    copy: Object,
    maxPickerFileSize: { type: Number, default: 50 * 1024 * 1024 }
  }

  connect() {
    this.entries = []
    this.submitting = false
    this.syncFormState()
  }

  disconnect() {
    this.entries.forEach((entry) => {
      entry.detectAbort?.abort()
      entry.createXhr?.abort()
      entry.storeXhr?.abort()
      if (!this.submitting) this.cancelBlob(entry)
      entry.resizeObserver?.disconnect()
    })
  }

  addFile(event) {
    if (event.detail.form !== this.element) return

    const { file, card } = event.detail
    const entry = {
      id: card.dataset.fileId,
      file,
      card,
      controls: card.querySelector(".target-picker-controls"),
      preview: card.querySelector(".target-picker-preview"),
      overlay: card.querySelector(".target-picker-overlay"),
      state: "uploading",
      faces: [],
      mode: "auto",
      targetFaceIndex: null,
      removed: false
    }
    this.entries.push(entry)
    this.observePreview(entry)

    if (this.isPickerImage(file) && file.size > this.maxPickerFileSizeValue) {
      this.failEntry(entry, this.text("target_too_large"), false)
      return
    }

    this.renderProgress(entry, this.text("uploading"), 0)
    this.startUpload(entry)
    this.syncFormState()
  }

  removeFile(event) {
    if (event.detail.form !== this.element) return

    const entry = this.entries.find((candidate) => candidate.file === event.detail.file)
    if (!entry) return

    entry.removed = true
    entry.detectAbort?.abort()
    entry.createXhr?.abort()
    entry.storeXhr?.abort()
    entry.resizeObserver?.disconnect()
    this.cancelBlob(entry)
    this.entries = this.entries.filter((candidate) => candidate !== entry)
    this.syncFormState()
  }

  prepareSubmit(event) {
    if (!this.allReady()) {
      event.preventDefault()
      this.setStatus(this.text("wait_until_ready"), "error")
      this.statusTarget.focus()
      return
    }

    this.submitting = true
    this.writeSubmissionPayload()
  }

  select(event) {
    const entry = this.entries.find((candidate) => candidate.id === event.currentTarget.dataset.entryId)
    if (!entry || entry.state !== "ready") return

    const selection = selectTargetFace(
      entry.faces,
      event.currentTarget.dataset.mode,
      event.currentTarget.dataset.faceIndex
    )
    if (!selection) return

    entry.mode = selection.mode
    entry.targetFaceIndex = selection.targetFaceIndex
    this.renderSelection(entry)
    this.syncFormState()
  }

  retry(event) {
    const entry = this.entries.find((candidate) => candidate.id === event.currentTarget.dataset.entryId)
    if (!entry || entry.removed) return

    if (entry.signedId) {
      this.detectFaces(entry)
    } else {
      this.startUpload(entry)
    }
  }

  startUpload(entry) {
    entry.state = "uploading"
    entry.retryable = true
    this.renderProgress(entry, this.text("uploading"), 0)
    this.syncFormState()

    const delegate = {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        entry.createXhr = xhr
      },
      directUploadWillStoreFileWithXHR: (xhr) => {
        entry.storeXhr = xhr
        xhr.upload.addEventListener("progress", ({ loaded, total }) => {
          if (!entry.removed && total > 0) {
            this.renderProgress(entry, this.text("uploading"), Math.round((loaded / total) * 100))
          }
        })
      }
    }
    entry.directUpload = new DirectUpload(entry.file, this.directUploadUrlValue, delegate)
    entry.directUpload.create((error, blob) => {
      if (error) {
        if (!entry.removed) this.failEntry(entry, this.text("upload_failed"), true)
        return
      }

      entry.signedId = blob.signed_id
      if (entry.removed) {
        this.cancelBlob(entry)
        return
      }
      if (this.isPickerImage(entry.file)) {
        this.detectFaces(entry)
      } else {
        entry.state = "ready"
        entry.mode = "auto"
        this.renderAutomatic(entry)
        this.syncFormState()
      }
    })
  }

  detectFaces(entry) {
    entry.state = "detecting"
    entry.detectionRequestId = (entry.detectionRequestId || 0) + 1
    const requestId = entry.detectionRequestId
    entry.detectAbort?.abort()
    entry.detectAbort = new AbortController()
    this.renderProgress(entry, this.text("finding_faces"), null)
    this.syncFormState()

    const body = new FormData()
    body.append("blob_signed_id", entry.signedId)

    fetch(this.detectUrlValue, {
      method: "POST",
      body,
      signal: entry.detectAbort.signal,
      headers: { "X-CSRF-Token": this.csrfToken() }
    })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!currentDetectionResponse(entry.detectionRequestId, requestId, entry.removed)) return
        if (json.blob_signed_id) {
          entry.signedId = json.blob_signed_id
        }
        if (!ok || json.status !== "ok") {
          this.failEntry(entry, json.message || this.text("detection_failed"), json.retryable !== false)
          return
        }

        entry.token = json.target_upload_token
        entry.faces = normalizeDetectedFaces(json.faces)
        entry.dimensions = { width: Number(json.width), height: Number(json.height) }
        if (entry.faces.length === 0) {
          this.failEntry(entry, this.text("no_faces"), false)
          return
        }

        const selection = initialTargetFaceSelection(entry.faces)
        entry.mode = selection.mode
        entry.targetFaceIndex = selection.targetFaceIndex
        entry.state = "ready"
        this.renderSelection(entry)
        this.syncFormState()
      })
      .catch((error) => {
        if (!currentDetectionResponse(entry.detectionRequestId, requestId, entry.removed) || error.name === "AbortError") return
        this.failEntry(entry, this.text("inspection_failed"), true)
      })
  }

  renderProgress(entry, label, percent) {
    entry.controls.replaceChildren()
    const labelElement = document.createElement("p")
    labelElement.className = "text-sm font-medium text-gray-200"
    labelElement.textContent = label
    entry.controls.appendChild(labelElement)

    const track = document.createElement("div")
    track.className = "mt-3 h-2 overflow-hidden rounded-full bg-gray-800"
    const bar = document.createElement("div")
    bar.className = percent === null
      ? "h-full w-1/3 animate-pulse rounded-full bg-blue-500"
      : "h-full rounded-full bg-blue-500 transition-all duration-200"
    if (percent !== null) bar.style.width = `${percent}%`
    track.appendChild(bar)
    entry.controls.appendChild(track)
    this.setStatus(label)
  }

  renderAutomatic(entry) {
    entry.overlay.replaceChildren()
    entry.controls.replaceChildren()
    const heading = document.createElement("p")
    heading.className = "text-sm font-semibold text-white"
    heading.textContent = this.text("automatic_title")
    const description = document.createElement("p")
    description.className = "mt-1 text-xs leading-5 text-gray-400"
    description.textContent = this.text("automatic_description")
    entry.controls.append(heading, description)
  }

  renderSelection(entry) {
    entry.controls.replaceChildren()

    const heading = document.createElement("div")
    heading.className = "flex items-center justify-between gap-3"
    const title = document.createElement("p")
    title.className = "text-sm font-semibold text-white"
    title.textContent = this.text(
      entry.faces.length === 1 ? "faces_found_one" : "faces_found_other",
      { count: entry.faces.length }
    )
    const order = document.createElement("span")
    order.className = "rounded-full bg-gray-800 px-2 py-1 text-[11px] font-medium text-gray-300"
    order.textContent = this.text("left_to_right")
    heading.append(title, order)

    const instructions = document.createElement("p")
    instructions.className = "mt-2 text-xs leading-5 text-gray-400"
    instructions.textContent = this.text("selection_instructions")

    const options = document.createElement("div")
    options.className = "mt-3 flex flex-wrap gap-2"
    options.appendChild(this.selectionButton(entry, "auto", null, this.text("auto")))
    options.appendChild(this.selectionButton(entry, "all", null, this.text("all_faces")))
    entry.faces.forEach((face) => {
      options.appendChild(this.selectionButton(
        entry,
        "index",
        Number(face.index),
        this.text("face_label", { number: Number(face.index) + 1 })
      ))
    })

    const summary = document.createElement("p")
    summary.className = "mt-3 text-xs font-medium text-blue-200"
    summary.textContent = this.selectionSummary(entry)
    entry.controls.append(heading, instructions, options, summary)
    this.renderFaceBoxes(entry)
  }

  selectionButton(entry, mode, faceIndex, label) {
    const selected = entry.mode === mode && (mode !== "index" || entry.targetFaceIndex === faceIndex)
    const button = document.createElement("button")
    button.type = "button"
    button.dataset.action = "web-face-switcher-target-picker#select"
    button.dataset.entryId = entry.id
    button.dataset.mode = mode
    if (faceIndex !== null) button.dataset.faceIndex = String(faceIndex)
    button.setAttribute("aria-pressed", selected ? "true" : "false")
    button.className = selected
      ? "min-h-11 rounded-lg border border-blue-300 bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      : "min-h-11 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm font-medium text-gray-200 transition hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
    button.textContent = label
    return button
  }

  renderFaceBoxes(entry) {
    entry.overlay.replaceChildren()
    const frame = this.previewFrame(entry)
    if (!frame) return

    entry.faces.forEach((face) => {
      const [left, top, right, bottom] = Array(face.bbox).map(Number)
      if (![left, top, right, bottom].every(Number.isFinite)) return

      const button = this.selectionButton(entry, "index", Number(face.index), String(Number(face.index) + 1))
      const selected = entry.mode === "index" && entry.targetFaceIndex === Number(face.index)
      button.className = [
        "pointer-events-auto absolute flex min-h-9 min-w-9 items-start justify-start rounded-md border-2 px-2 py-1 text-xs font-bold text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-200",
        selected ? "border-blue-300 bg-blue-600/70" : "border-white bg-gray-950/45"
      ].join(" ")
      button.setAttribute("aria-label", this.text("select_face_aria", { number: Number(face.index) + 1 }))
      button.style.left = `${frame.left + (left * frame.scale)}px`
      button.style.top = `${frame.top + (top * frame.scale)}px`
      button.style.width = `${Math.max((right - left) * frame.scale, 36)}px`
      button.style.height = `${Math.max((bottom - top) * frame.scale, 36)}px`
      entry.overlay.appendChild(button)
    })
  }

  previewFrame(entry) {
    const sourceWidth = entry.dimensions?.width
    const sourceHeight = entry.dimensions?.height
    const displayedWidth = entry.preview.clientWidth
    const displayedHeight = entry.preview.clientHeight
    if (![sourceWidth, sourceHeight, displayedWidth, displayedHeight].every((value) => Number(value) > 0)) return null

    const scale = Math.min(displayedWidth / sourceWidth, displayedHeight / sourceHeight)
    return {
      scale,
      left: (displayedWidth - (sourceWidth * scale)) / 2,
      top: (displayedHeight - (sourceHeight * scale)) / 2
    }
  }

  failEntry(entry, message, retryable) {
    entry.state = "error"
    entry.retryable = retryable
    entry.token = null
    entry.controls.replaceChildren()

    const error = document.createElement("p")
    error.className = "text-sm font-medium text-red-300"
    error.textContent = message
    entry.controls.appendChild(error)

    if (retryable) {
      const retry = document.createElement("button")
      retry.type = "button"
      retry.dataset.action = "web-face-switcher-target-picker#retry"
      retry.dataset.entryId = entry.id
      retry.className = "mt-3 min-h-11 rounded-lg border border-red-400/60 px-3 py-2 text-sm font-semibold text-red-100 hover:bg-red-950/40 focus:outline-none focus:ring-2 focus:ring-red-300"
      retry.textContent = this.text("try_again")
      entry.controls.appendChild(retry)
    }

    this.setStatus(message, "error")
    this.syncFormState()
  }

  writeSubmissionPayload() {
    const submission = serializeTargetPickerSubmission(this.entries.map((entry) => ({
      signedId: entry.signedId,
      pickerImage: this.isPickerImage(entry.file),
      token: entry.token,
      mode: entry.mode,
      targetFaceIndex: entry.targetFaceIndex
    })))

    this.hiddenFieldsTarget.querySelectorAll("[data-target-media-signed-id]").forEach((input) => input.remove())
    submission.signedIds.forEach((signedId) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "face_switching_batch[target_media][]"
      input.value = signedId
      input.dataset.targetMediaSignedId = "true"
      this.hiddenFieldsTarget.appendChild(input)
    })
    this.hiddenFieldsTarget.querySelector('[data-target-face-selections]').value = JSON.stringify(submission.selections)
  }

  syncFormState() {
    this.writeSubmissionPayload()
    const ready = this.allReady()
    const creditDisabled = this.submitButtonTarget.dataset.creditDisabled === "true"
    this.submitButtonTarget.disabled = !ready || creditDisabled

    if (this.entries.length === 0) {
      this.setStatus(this.text("empty_status"))
    } else if (ready) {
      this.setStatus(this.text("ready_status"), "success")
    }
  }

  allReady() {
    return this.entries.length > 0 && this.entries.every((entry) => entry.state === "ready")
  }

  isPickerImage(file) {
    return supportsPickerImage(file)
  }

  selectionSummary(entry) {
    if (entry.mode === "all") return this.text("selected_all")
    if (entry.mode === "index") return this.text("selected_face", { number: entry.targetFaceIndex + 1 })
    return this.text("selected_auto")
  }

  text(key, replacements = {}) {
    let value = this.copyValue[key]
    Object.entries(replacements).forEach(([name, replacement]) => {
      value = value.replaceAll(`%{${name}}`, String(replacement))
    })
    return value
  }

  setStatus(message, type = "neutral") {
    this.statusTarget.textContent = message
    this.statusTarget.className = type === "error"
      ? "mt-3 rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200"
      : type === "success"
        ? "mt-3 rounded-lg border border-emerald-800 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200"
        : "mt-3 rounded-lg border border-gray-800 bg-gray-950/40 px-4 py-3 text-sm text-gray-300"
  }

  cancelBlob(entry) {
    if (!entry.signedId || entry.submitted) return

    const body = new FormData()
    body.append("blob_signed_id", entry.signedId)
    fetch(this.cancelUrlValue, {
      method: "DELETE",
      body,
      keepalive: true,
      headers: { "X-CSRF-Token": this.csrfToken() }
    }).catch(() => {})
  }

  observePreview(entry) {
    if (!window.ResizeObserver) return

    entry.resizeObserver = new ResizeObserver(() => {
      if (entry.state === "ready" && entry.faces.length > 0) this.renderFaceBoxes(entry)
    })
    entry.resizeObserver.observe(entry.preview)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
